import React, { useMemo } from 'react';
import { ProductV2AvailabilityStatus } from '@brandclub/types';
import type { ConciergeProductV2Detail } from '../../types';
import type { CatalogV2Variant } from '../../../types/catalogV2Product';
import { VariantOptionRow } from './VariantOptionRow';

/** A real, customer-facing variation dimension resolved from the catalog bag. */
interface VariationDimension {
  /** Actual `variationNameValues` key backing this dimension, e.g. `sizeName`. */
  key: string;
  /** Friendly label shown to the user, e.g. "Size". */
  label: string;
  /** Distinct option values in first-seen order. */
  values: string[];
}

/**
 * Resolve the customer-facing variation dimensions from the variant list. The
 * V2 catalog carries each variant's option map as the retailer's own option
 * names (e.g. `Count`, `Weight`, `Size`), so we build a dimension for every
 * option key present across the family, collect its distinct values in
 * first-seen order, and drop single-value dimensions (not a real choice). The
 * label is the option name itself.
 */
function getVariationDimensions(detail: ConciergeProductV2Detail): VariationDimension[] {
  const variants = detail.variants ?? [];
  if (variants.length === 0) return [];

  // Order the dimension rows by the VIEWED product's own option axes first — a
  // stable order that matches its catalog `variation.axes`. Seeding from the
  // family instead would follow whichever sibling the catalog returns first (an
  // arbitrary sort whose axis order isn't guaranteed to match this product), so
  // the rows could invert between products. Extra keys only some siblings carry
  // are appended after.
  const keysInOrder: string[] = [];
  for (const key of Object.keys(detail.variationNameValues ?? {})) {
    if (!keysInOrder.includes(key)) keysInOrder.push(key);
  }
  for (const variant of variants) {
    for (const key of Object.keys(variant.variationNameValues ?? {})) {
      if (!keysInOrder.includes(key)) keysInOrder.push(key);
    }
  }

  // Values are collected in the family's (arbitrary catalog/ES) return order, so a
  // numeric axis comes out jumbled (Count "1,12,6,3"; Weight "23-44,5-22,…"). Sort
  // number-led values ascending by their leading number (handles both "12" and
  // "5-22lbs"); leave non-numeric axes (e.g. Color) in first-seen order since they
  // have no natural order and shouldn't be alphabetized away from the merchant's.
  const naturalCompare = (a: string, b: string) =>
    a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' });
  const isNumberLed = (value: string) => /^\s*\d/.test(value);

  const dimensions: VariationDimension[] = [];
  for (const key of keysInOrder) {
    const values: string[] = [];
    for (const variant of variants) {
      const value = variant.variationNameValues?.[key];
      if (value && !values.includes(value)) values.push(value);
    }
    if (values.length > 1) {
      const ordered = values.every(isNumberLed) ? [...values].sort(naturalCompare) : values;
      dimensions.push({ key, label: key, values: ordered });
    }
  }
  return dimensions;
}

export function VariantSelectors({
  detail,
  onSelect,
}: {
  detail: ConciergeProductV2Detail;
  onSelect: (variant: CatalogV2Variant) => void;
}) {
  const dimensions = useMemo(() => getVariationDimensions(detail), [detail]);
  if (dimensions.length === 0) return null;

  // Find the variant for one dimension's value while holding every OTHER currently
  // selected dimension fixed. A match means switching this dimension preserves the
  // rest; no match means the combination doesn't exist (a dead-end), so the chip is
  // disabled (Amazon-style availability).
  const findVariant = (key: string, value: string) =>
    detail.variants?.find(
      (variant) =>
        variant.variationNameValues?.[key] === value &&
        Object.entries(detail.variationNameValues ?? {}).every(
          ([otherKey, otherValue]) => otherKey === key || variant.variationNameValues?.[otherKey] === otherValue,
        ),
    );

  return (
    <>
      {dimensions.map(({ key, label, values }) => {
        const selected = detail.variationNameValues?.[key];
        // Resolve availability up front so unavailable chips (out of stock, or a
        // combination that doesn't exist) can be pushed to the end of the row.
        // `sort` is stable, so each group keeps its original order. This matters
        // more now the row collapses to two lines: the options a shopper can
        // actually pick are the ones that stay visible.
        const orderedValues = values
          .map((value) => {
            const variant = findVariant(key, value);
            const isAvailable =
              !!variant &&
              variant.isAvailable !== false &&
              variant.availabilityStatus !== ProductV2AvailabilityStatus.OutOfStock;
            return { value, variant, isAvailable };
          })
          .sort((a, b) => Number(b.isAvailable) - Number(a.isAvailable));
        return (
          <VariantOptionRow key={key} label={label} selected={selected} options={orderedValues} onSelect={onSelect} />
        );
      })}
    </>
  );
}
